import { createClient } from '@iq/services';
import { getAccessToken } from '@iq/react-components';

import config from './config';

const services = createClient({
  ...(config ? config.services : null),
  onRequestToken: process.env.NODE_ENV === 'test' ? () => '' : getAccessToken,
});

export function getServiceToken(type) {
  return services[type].getToken();
}

/** ********************************************
 *                                             *
 *               Auth                          *
 *                                             *
 ******************************************** */

export function getGrants(oid) {
  return services.auth.grants(`users/${oid}`);
}

export function getMemberTypes() {
  return services.auth.getMemberTypes();
}

export function createMember(data) {
  return services.auth.createMember(data);
}

export function updateMember(memberId, data) {
  return services.auth.updateMember(memberId, data);
}

export function deleteMember(memberId) {
  return services.auth.deleteMember(memberId);
}

export function getMembers(data) {
  return services.auth.getMembers(data);
}

export function deleteMemberConnection(memberId, userId) {
  return services.auth.deleteMemberConnection(memberId, userId);
}

export function getInvitations(query) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.auth.getInvitations(query);
}

export function createInvitation(data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Invitations not available in on-premise mode'));
  return services.auth.createInvitation(data);
}

export function updateInvitation(invitationId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Invitations not available in on-premise mode'));
  return services.auth.updateInvitation(invitationId, data);
}

export function deleteInvitation(invitationId) {
  if (config?.onPremise)
    return Promise.reject(new Error('Invitations not available in on-premise mode'));
  return services.auth.deleteInvitation(invitationId);
}

export function authorizeUser(data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Invitations not available in on-premise mode'));
  return services.auth.authorizeUser(data);
}

export function getOneUser(userId) {
  return services.auth.getOneUser(userId);
}

export function getOneUserLimited(userId) {
  return services.auth.getOneUserLimited(userId);
}

export function getSelf(query) {
  return services.auth.getSelf(query);
}

export function getUsers(query) {
  return services.auth.getUsers(query);
}

export function getLimitedUsers(query) {
  return services.auth.getLimitedUsers(query);
}

export function deleteUser(userId) {
  return services.auth.deleteUser(userId);
}

export function getServiceAccounts() {
  return services.auth.getServiceAccounts();
}

export function transferServiceAccountPermissions(data) {
  return services.auth.transferServiceAccountPermissions(data);
}

export function getAuthServiceDocuments() {
  return services.auth.getDocs();
}

/**
 * Fetch client configuration (mapbox token, appInsights, etc.) from auth service.
 * This is called after authentication so secrets are never exposed publicly.
 * @returns {Promise<{mapboxAccessToken: string, applicationInsights: object}>}
 */
export function getConfiguration(query) {
  return services.auth.getConfiguration(query);
}

/** ********************************************
 *                                             *
 *               Collaboration                 *
 *                                             *
 ******************************************** */

export function getTags(query) {
  return services.collaboration.getTags(query);
}

export function createTag(data) {
  return services.collaboration.createTag(data);
}

export function updateTag(tagId, data) {
  return services.collaboration.updateTag(tagId, data);
}

export function deleteTag(id) {
  return services.collaboration.deleteTag(id);
}

export function getSystemMessages() {
  if (config?.onPremise)
    return Promise.resolve({
      values: [],
      total: 0,
    });
  return services.collaboration.getSystemMessages();
}

export function getCollaborationServiceDocuments() {
  return services.collaboration.getDocs();
}

export function getComments(query) {
  return services.collaboration.getComments(query);
}

export function createComment(data) {
  return services.collaboration.createComment(data);
}

export function updateComment(id, data) {
  return services.collaboration.updateComment(id, data);
}

export function deleteComment(id) {
  return services.collaboration.deleteComment(id);
}

/** ********************************************
 *                                             *
 *               Data                          *
 *                                             *
 ******************************************** */

export function getSources(siteId) {
  return services.data.getSources({ site: siteId });
}

export function importSource(sourceId) {
  return services.data.importSourceComponents(sourceId);
}

export function uploadAOExcel(sourceId, formData) {
  return services.data.importAOExcel(sourceId, formData);
}

export function updateSource(sourceId, data) {
  return services.data.updateSource(sourceId, data);
}

export function deleteSource(sourceId) {
  return services.data.deleteSource(sourceId);
}

export function createSource(data) {
  return services.data.createSource(data);
}

export function getSourceTypes() {
  return services.data.getSourceTypes();
}

export function getSourceHealth(siteId, org) {
  return services.data.getSourceHealth({ site: siteId, org });
}

export function getUniquePointGroups(query) {
  return services.data.getUniquePointGroups(query);
}

export function getComponents(siteId) {
  return services.data.getComponents({ site: siteId });
}

export function getSiteComponentTree(siteId) {
  return services.data.getSiteComponentTree({ site: siteId });
}

export function getComponentsStatus(siteId) {
  return services.data.getComponentsStatus({ site: siteId });
}

export function createComponent(data) {
  return services.data.createComponent(data);
}

export function upsertComponentsInBulk(formData) {
  return services.data.upsertComponentsInBulk(formData);
}

export function createVariables(sourceId, data) {
  return services.data.createVariables(sourceId, data);
}

export function updateVariable(sourceId, variableId, data) {
  return services.data.updateVariable(sourceId, variableId, data);
}

export function deleteVariable(sourceId, variableId) {
  return services.data.deleteVariable(sourceId, variableId);
}

export function updateComponent(componentId, data) {
  return services.data.updateComponent(componentId, data);
}

export function deleteComponent(componentId) {
  return services.data.deleteComponent(componentId);
}

export function deleteNonSiteComponents(query) {
  return services.data.deleteNonSiteComponents(query);
}

export function getSeries(data) {
  return services.data.getSeries(data);
}

export function getLastValues(data) {
  return services.data.getLastValues(data);
}

export function uploadEventsFile(formData, sourceId) {
  return services.data.injectEvents(formData, sourceId);
}

export function getTfrTemplates(query) {
  return services.data.getTfrTemplates(query);
}

export function createTfrTemplate(data) {
  return services.data.createTfrTemplate(data);
}

export function updateTfrTemplate(templateId, data) {
  return services.data.updateTfrTemplate(templateId, data);
}

export function deleteTfrTemplate(templateId) {
  return services.data.deleteTfrTemplate(templateId);
}

export function getEvents(query) {
  return services.data.getEvents(query);
}

export function getVariables(sourceId, query) {
  return services.data.getVariables(sourceId, query);
}

export function getEventBuckets(query) {
  return services.data.getEventBuckets(query);
}

export function getVariable(sourceId, variableId) {
  return services.data.getVariable(sourceId, variableId);
}

export function getVisualizations(query) {
  return services.data.getVisualizations(query);
}

export function createVisualization(data) {
  return services.data.createVisualization(data);
}

export function bulkUpdateVisualization(data) {
  return services.data.bulkUpdateVisualization(data);
}

export function updateVisualization(visualizationId, data) {
  return services.data.updateVisualization(visualizationId, data);
}

export function updateVisualizationsTags(data) {
  return services.data.updateVisualizationsTags(data);
}

export function deleteVisualization(visualizationId) {
  return services.data.deleteVisualization(visualizationId);
}

export function downloadComponentsExcelTemplate(query) {
  return services.data.exportComponents(query);
}

export function getVisualizationTemplateTypes() {
  return services.data.getVisualizationTemplateTypes();
}

export function uploadVisualizationsExcel(formData) {
  return services.data.importVisualizations(formData);
}

export function downloadVisualizationsTemplate(query) {
  return services.data.getVisualizationsTemplate(query);
}

export function createStateSet(data, site, org) {
  return services.data.createStateSet(data, site, org);
}

export function updateStateSet(stateSetId, data) {
  return services.data.updateStateSet(stateSetId, data);
}

export function getStateSets(query) {
  return services.data.getStateSets(query);
}

export function deleteStateSet(stateSet) {
  return services.data.deleteStateSet(stateSet);
}

export function checkConnectivity(sourceId) {
  return services.data.checkConnectivity(sourceId);
}

export function getDataServiceDocuments() {
  return services.data.getDocs();
}

export function updateTfrClassificationConfiguration(sourceId, configuration, fileName) {
  return services.data.updateTfrClassificationConfiguration(sourceId, configuration, fileName);
}

export function getSwitchgearCounterAggregates(sourceId, query) {
  return services.data.getSwitchgearCounterAggregates(sourceId, query);
}

export function updateSwitchgearCounterConfiguration(sourceId, configuration, fileName) {
  return services.data.updateSwitchgearCounterConfiguration(sourceId, configuration, fileName);
}

/** ********************************************
 *                                             *
 *               Event                         *
 *                                             *
 ******************************************** */

export function getEventTypes(siteId) {
  return services.event.getEventTypes(siteId);
}

export function getUserEvents(query) {
  return services.event.getEvents(query);
}

export function updateEventsTags(data) {
  return services.event.updateEventsTags(data);
}

export function deleteUserEvent(eventId) {
  return services.event.deleteEvent(eventId);
}

export function excludeSeriesEvent(data) {
  return services.event.excludeSeriesEvent(data);
}

export function bulkOverwriteEventSeries(data) {
  return services.event.bulkOverwriteEventSeries(data);
}

export function getEventServiceDocuments() {
  return services.event.getDocs();
}

/** ********************************************
 *                                             *
 *               Alarms                        *
 *                                             *
 ******************************************** */

export function getAlarms(query) {
  return services.event.getAlarms(query);
}

export function updateAlarm(alarmId, data) {
  return services.event.updateAlarm(alarmId, data);
}

export function deleteAlarm(alarmId) {
  return services.event.deleteAlarm(alarmId);
}

/** ********************************************
 *                                             *
 *          Notification Configs               *
 *                                             *
 ******************************************** */

export function getNotificationConfigs(query) {
  return services.event.getNotificationConfigs(query);
}

export function createNotificationConfig(data) {
  return services.event.createNotificationConfig(data);
}

export function updateNotificationConfig(id, data) {
  return services.event.updateNotificationConfig(id, data);
}

export function deleteNotificationConfig(id) {
  return services.event.deleteNotificationConfig(id);
}

/** ********************************************
 *                                             *
 *               File                          *
 *                                             *
 ******************************************** */

export function getFiles(query) {
  if (config?.onPremise) return Promise.resolve({ values: [], pages: 0, total: 0 });
  return services.file.getFiles(query);
}

export function getFile(fileId) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.getFile(fileId);
}

export function getInstructionals(query) {
  if (config?.onPremise) return Promise.resolve({ values: [], total: 0 });
  return services.file.getInstructionals(query);
}

export function deleteFile(fileId) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.deleteFile(fileId);
}

export function createBookmark(fileId, body) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.createBookmark(fileId, body);
}

export function deleteBookmark(fileId, bookmarkId) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.deleteBookmark(fileId, bookmarkId);
}

export function updateBookmark(fileId, bookmarkId, body) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.updateBookmark(fileId, bookmarkId, body);
}

export function uploadFile(formData) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.createFile(formData);
}

export function updateFile(fileId, body) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.updateFile(fileId, body);
}

export function updateFilesAndBookmarksTags(body) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.updateTagsInFilesAndBookmarks(body);
}

export function getTypes(query) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.file.getTypes(query);
}

export function getFileServiceDocuments() {
  if (config?.onPremise) return Promise.resolve(null);
  return services.file.getDocs();
}

export function downloadFileBlob(fileId) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.getFileBlob(fileId);
}

export function downloadZippedFiles(query) {
  if (config?.onPremise)
    return Promise.reject(new Error('File service not available in on-premise mode'));
  return services.file.downloadZippedFiles(query);
}
export function getFileThumbnail(fileId) {
  if (config?.onPremise) return Promise.resolve(null);
  return services.file.getFileThumbnail(fileId);
}

/** ********************************************
 *                                             *
 *               Integration                   *
 *                                             *
 ******************************************** */

export function getRemoteAssetInfo(integrationId, componentId) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.getRemoteAssetInfo(integrationId, componentId);
}

export function createRemoteEvent(integrationId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.createRemoteEvent(integrationId, data);
}

export function getIntegrations(query) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.integration.getIntegrations(query);
}

export function getIntegrationTypes() {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.integration.getIntegrationTypes();
}

export function createIntegration(data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.createIntegration(data);
}

export function createIntegrationTemplate(data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.createIntegrationTemplate(data);
}

export function updateIntegration(integrationId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.updateIntegration(integrationId, data);
}

export function updateIntegrationTemplate(integrationId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.updateIntegrationTemplate(integrationId, data);
}

export function deleteIntegration(integrationId) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.deleteIntegration(integrationId);
}

export function syncRemoteAssets(integrationId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.syncRemoteAssets(integrationId, data);
}

export function createIntegratedSource(integrationId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.createSource(integrationId, data);
}

export function updateIntegratedSource(integrationId, sourceId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Integration service not available in on-premise mode'));
  return services.integration.updateSource(integrationId, sourceId, data);
}

export function getIntegratedSources(integrationId, query) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.integration.getSources(integrationId, query);
}

export function getIntegrationServiceDocuments() {
  if (config?.onPremise) return Promise.resolve(null);
  return services.integration.getDocs();
}

/** ********************************************
 *                                             *
 *               Model                         *
 *                                             *
 ******************************************** */

export function getModels(siteId) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.model.getModels({ site: siteId });
}

export function createModel(body) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.createModel(body);
}

export function updateModel(modelId, body) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.updateModel(modelId, body);
}

export function createModelVersion(modelId, body) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.createVersion(modelId, body);
}

export function deleteModel(modelId) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.deleteModel(modelId);
}

export function getSiteMappings(siteId) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.model.getSiteMappings(siteId);
}

export function createMapping(modelId, versionId, mapping) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.createMapping(modelId, versionId, mapping);
}

export function upsertMappingsInBulk(modelId, versionId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.upsertMappingsInBulk(modelId, versionId, data);
}

export function updateOrDeleteMappings(modelId, versionId, scope) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.deleteMappings(modelId, versionId, scope);
}

export function updateMapping(modelId, versionId, mappingId, mapping) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.updateMapping(modelId, versionId, mappingId, mapping);
}

export function deleteMapping(modelId, versionId, mappingId) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.deleteMapping(modelId, versionId, mappingId);
}

export function updateSceneConfig(modelId, versionId, sceneConfig) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.updateSceneConfig(modelId, versionId, sceneConfig);
}

export async function getModelThumbnail(modelId, versionId) {
  if (config?.onPremise) return null;
  return services.model.getThumbnail(modelId, versionId);
}

export function getGeometries(modelId) {
  if (config?.onPremise) return Promise.resolve({ values: [] });
  return services.model.getGeometries(modelId);
}

export function createOrUpdateGeometries(modelId, data) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.createOrUpdateGeometries(modelId, data);
}
export function deleteGeometry(modelId, geometryId) {
  if (config?.onPremise)
    return Promise.reject(new Error('Models not available in on-premise mode'));
  return services.model.deleteGeometry(modelId, geometryId);
}

export function getModelServiceDocuments() {
  if (config?.onPremise) return Promise.resolve(null);
  return services.model.getDocs();
}

/** ********************************************
 *                                             *
 *               Site                          *
 *                                             *
 ******************************************** */

export async function getAllSitesData() {
  return services.site.getSites();
}

export function createSite(body) {
  return services.site.createSite(body);
}

export function updateSite(siteId, site) {
  return services.site.updateSite(siteId, site);
}

export function deleteSite(siteId) {
  return services.site.deleteSite(siteId);
}

export function updateLayout(siteId, layoutId, layout) {
  return services.site.updateLayout(siteId, layoutId, layout);
}

export function lookupSite(type, thirdPartyId, query) {
  return services.site.lookupSite(type, thirdPartyId, query);
}

export function uploadSitesExcel(file) {
  if (config?.onPremise)
    return Promise.reject(new Error('Bulk site upload not available in on-premise mode'));
  return services.site.uploadSitesExcel(file);
}

export function downloadSitesExcelTemplate() {
  if (config?.onPremise)
    return Promise.reject(new Error('Bulk site export not available in on-premise mode'));
  return services.site.downloadSitesExcelTemplate();
}

export function getSiteServiceDocuments() {
  return services.site.getDocs();
}

export function removeVisualizationFromLayouts(siteId, visualizationName) {
  return services.site.removeVisualization(siteId, { visualizationName });
}

export function updateVisualizationInLayouts(siteId, oldVisualizationName, newVisualizationName) {
  return services.site.updateVisualization(siteId, { oldVisualizationName, newVisualizationName });
}

export default services;
